// LEGACY: Uses @ion/components. New code should use @metarouter-private/mr-mui components instead.
import React from 'react';
import pt from 'prop-types';

import { formValidators, MrInputText, MrInputTextarea, MrInputPassword, MrInputNumber } from '@ion/components';

import { useFormContext } from 'app/context/form-context';
import YamlCodeEditor from 'app/components/YamlCodeEditor';

import Compression from './compression';
import Sasl from './Sasl';
import S3 from './s3';
import Gcs from './Gcs';
import Stage from './Stage';
import {
  BIG_QUERY_OPTIONS,
  TABLE_OVERRIDES,
  FIELD_OVERRIDES,
  PARTITIONING,
} from 'app/playbook-editor/parameters/editor-panel/parameter/consts';
import BigQueryOptions from './BigQueryOptions';
import {
  BROKERS_PLACEHOLDER,
  TABLE_OVERRIDES_PLACEHOLDER,
  FIELD_OVERRIDES_PLACEHOLDER,
  PARTITIONING_PLACEHOLDER,
} from './placeholders';
import Tls from './Tls';
import validateYaml from './validateYaml';

const { validateRequired } = formValidators;

// Ace only grows with content, so an empty editor collapses to one line and hides the placeholder.
// The editor's own "Invalid Yaml" message never reaches form state, so submit needs its own validator.
const yamlEditorProps = ({ placeholder, isOptional }) => ({
  placeholder,
  options: { minLines: placeholder.split('\n').length },
  validators: isOptional ? [validateYaml()] : [validateRequired(), validateYaml()],
});

const ParamLookup = ({
  isPipelineVar,
  opId,
  exampleValue,
  isOptional,
  defaultValue,
  _path,
  name: paramName,
  saslType,
  stageType,
  username,
  password,
  algorithm,
  region,
  bucket,
  prefix,
  accessKey,
  secretKey,
  serverSideEncryption,
  compression,
  credentialJson,
}) => {
  const { validateField, resetError, formState, setField } = useFormContext();
  const name = `${opId}-defaultValue-${_path}`;

  let Component;
  let extraProps = {};

  switch (paramName) {
    case 'S_3':
      return (
        <S3
          opId={opId}
          _path={_path}
          region={region}
          bucket={bucket}
          prefix={prefix}
          accessKey={accessKey}
          secretKey={secretKey}
          serverSideEncryption={serverSideEncryption}
          compression={compression}
        />
      );
    case 'GCS':
      return (
        <Gcs
          opId={opId}
          _path={_path}
          bucket={bucket}
          prefix={prefix}
          credentialJson={credentialJson}
          compression={compression}
        />
      );
    case 'AUTHENTICATION (SASL)':
      return (
        <Sasl
          opId={opId}
          _path={_path}
          saslType={saslType}
          username={username}
          password={password}
          algorithm={algorithm}
        />
      );
    case 'STAGE':
      return (
        <Stage
          opId={opId}
          _path={_path}
          stageType={stageType}
          region={region}
          bucket={bucket}
          prefix={prefix}
          accessKey={accessKey}
          secretKey={secretKey}
          serverSideEncryption={serverSideEncryption}
          compression={compression}
          credentialJson={credentialJson}
        />
      );
    case 'COMPRESSION':
      return <Compression name={name} defaultValue={defaultValue} isOptional={isOptional} />;
    case BIG_QUERY_OPTIONS: {
      return <BigQueryOptions name={name} defaultValue={defaultValue} isOptional={isOptional} />;
    }
    case 'OPT_OUT_SCHEMA_CREATION':
    case 'TLS': {
      return <Tls name={name} defaultValue={defaultValue} />;
    }
    case TABLE_OVERRIDES:
      Component = YamlCodeEditor;
      extraProps = yamlEditorProps({ placeholder: TABLE_OVERRIDES_PLACEHOLDER, isOptional });
      break;
    case FIELD_OVERRIDES:
      Component = YamlCodeEditor;
      extraProps = yamlEditorProps({ placeholder: FIELD_OVERRIDES_PLACEHOLDER, isOptional });
      break;
    case PARTITIONING:
      Component = YamlCodeEditor;
      extraProps = yamlEditorProps({ placeholder: PARTITIONING_PLACEHOLDER, isOptional });
      break;
    case 'BACKLOG_COUNT':
    case 'PROFILE_ID':
    case 'BATCH_SIZE':
      Component = MrInputNumber;
      break;
    case 'PASSWORD':
      Component = MrInputPassword;
      break;
    case 'PRIVATE_KEY':
    case 'CREDENTIAL_JSON':
    case 'CREDENTIALS':
    case 'CREDENTIALS_JSON':
      Component = MrInputTextarea;
      extraProps = {
        fullWidth: true,
        rows: 8,
      };
      break;
    case 'BROKERS':
      Component = MrInputText;
      extraProps = {
        fullWidth: true,
        placeholder: BROKERS_PLACEHOLDER,
      };
      break;
    default:
      Component = MrInputText;
      break;
  }

  return (
    <Component
      onBlur={validateField}
      onInput={resetError}
      name={name}
      value={isPipelineVar ? '' : formState[name]?.value ?? defaultValue ?? ''}
      onChange={setField}
      errorMsg={isPipelineVar ? null : formState[name]?.errorMsg}
      placeholder={exampleValue}
      validators={!isOptional ? [validateRequired()] : []}
      disabled={isPipelineVar}
      {...extraProps}
    />
  );
};

ParamLookup.propTypes = {
  opId: pt.string.isRequired,
  name: pt.string.isRequired,
  _path: pt.string.isRequired,
  isPipelineVar: pt.bool,
  exampleValue: pt.string,
  isOptional: pt.bool,
  defaultValue: pt.oneOfType([pt.string, pt.number]),
  saslType: pt.string,
  stageType: pt.string,
  password: pt.string,
  username: pt.string,
  algorithm: pt.string,
  region: pt.string,
  bucket: pt.string,
  prefix: pt.string,
  accessKey: pt.string,
  secretKey: pt.string,
  serverSideEncryption: pt.string,
  compression: pt.string,
  credentialJson: pt.string,
};

export default ParamLookup;
